import { ReactElement, useState } from "react";
import {
  FormControl, IconButton, MenuItem,
  Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip
} from "@mui/material";
import { IAnnotation } from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getPalette, getPaletteColor } from "../../../configuration/configuration";
import StyledTableRow from "../../../components/styledTableRow";
import Select from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";
import ColorPicker from "../../../components/colorPicker";

interface IProps {
  annotations: IAnnotation[];
  setAnnotations: (annotations: IAnnotation[]) => void;
}

const AnnotationsPanel = ({ annotations, setAnnotations }: IProps): ReactElement => {
  const [ colors, setColors ] = useState<string[]>( annotations.map((v: IAnnotation) => v.color));

  const addAnnotation  = () => {
    const color = getPaletteColor();
    setColors([ ...colors, color]);

    setAnnotations([ ...annotations, {
      title: "Annotation",
      color: `#${color}`,
      value: 0,
      comparison: "="
    } ]);
  };

  const removeAnnotation  = (index: number) => {
    annotations.splice(index, 1);
    setAnnotations([ ...annotations ]);

    const newColors = [ ...colors];
    newColors.splice(index, 1);
    setColors(newColors);
  };

  const updateItemsAnnotation = (index: number, field: keyof IAnnotation, value: any) => {
    const annotation = annotations[index];
    (annotation[field] as any) = value;

    if (field === "color") {
      const newColors = [...colors];
      newColors[index] = value;
      setColors(newColors);
      annotation[field] = newColors[index];
    } else if (field === "comparison") {
      annotation.title = value === "MIN" ? "Minimum" : "Maximum";
    }

    setAnnotations([ ...annotations ]);
  };

  return (
    <TableContainer sx={{ maxHeight: 270 }}>
      <Table stickyHeader={true}  size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ minWidth: 180 }}>
              <IconButton sx={{ padding: 2 }} onClick={addAnnotation} disabled={annotations.length > 4}>
                <Tooltip title={"Add annotation"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
              Annotation
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Comparison</TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Value</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}>Color</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}/>
          </TableRow>
        </TableHead>
        <TableBody>
          { annotations.map((row, index) => {

            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                <TableCell>
                  <TextField
                    variant="standard"
                    size="small"
                    value={row.title}
                    disabled={row.comparison === "MIN" || row.comparison === "MAX"}
                    onChange={(event) => updateItemsAnnotation(index, "title",  event.target.value as string)}
                  />
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <Select
                      variant="standard"
                      value={row.comparison}
                      displayEmpty={true}
                      onChange={(event) => updateItemsAnnotation(index, "comparison", event.target.value as string)}>
                      <MenuItem value={">"}>&gt;</MenuItem>
                      <MenuItem value={">="}>&gt;=</MenuItem>
                      <MenuItem value={"<"}>&lt;</MenuItem>
                      <MenuItem value={"<="}>&lt;=</MenuItem>
                      <MenuItem value={"<>"}>&lt;&lt;</MenuItem>
                      <MenuItem value={"="}>=</MenuItem>
                      <MenuItem value={"MIN"}>MIN</MenuItem>
                      <MenuItem value={"MAX"}>MAX</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <TextField
                      variant="standard"
                      size="small"
                      value={row.value}
                      type="number"
                      disabled={row.comparison === "MIN" || row.comparison === "MAX"}
                      onChange={(event) => updateItemsAnnotation(index, "value",  parseFloat(event.target.value as string))}
                    />
                  </FormControl>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    {/*<input type="color" value={row.color} onChange={(event: ChangeEvent<HTMLInputElement>) => updateItemsAnnotation(index, "color", event.target.value as string)}/>*/}
                    { row.comparison !== "MIN" && row.comparison !== "MAX" &&
                      <ColorPicker color={colors[index]} onChange={(color) => updateItemsAnnotation(index, "color", color)} disableAlpha={true}/>
                    }
                  </FormControl>
                </TableCell>

                <TableCell>
                  <IconButton sx={{ padding: 2 }} onClick={() => removeAnnotation(index)} >
                    <Tooltip title={"Remove annotation"}>
                      <Delete style={{ color: getPalette().statusColor.error.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AnnotationsPanel;
